/**
 * Google Analytics 4 for the public marketing pages only (home, pricing,
 * products, security, blog). Never loaded inside the app or on link pages
 * (Secure Drop, signing, shares, file requests): their addresses carry
 * private access codes. Loaded only after the visitor accepts the cookie
 * banner, only on the production site, and paused whenever the visitor
 * leaves the marketing pages.
 */
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-K2L7GJF2VW';

const CONSENT_KEY = 'mdu-analytics-consent';
const PRODUCTION_HOSTS = ['mydocuploads.com', 'www.mydocuploads.com'];

/** 'granted', 'denied', or null when the visitor has not chosen yet. */
export function readConsent(storage = globalThis.localStorage) {
  try {
    const value = storage?.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

export function saveConsent(value, storage = globalThis.localStorage) {
  try {
    if (value) storage?.setItem(CONSENT_KEY, value);
    else storage?.removeItem(CONSENT_KEY);
  } catch {
    // Private windows can refuse storage; the banner simply asks again next time.
  }
}

export function analyticsAllowedHere({
  production = import.meta.env.PROD,
  hostname = globalThis.location?.hostname,
} = {}) {
  return Boolean(production) && PRODUCTION_HOSTS.includes(hostname);
}

const disableFlag = `ga-disable-${GA_MEASUREMENT_ID}`;

/** Loads gtag once, then (re)enables sending. Page views are sent by hand. */
export function enableAnalytics(win = window, doc = document) {
  if (!win.gtag) {
    win.dataLayer = win.dataLayer || [];
    win.gtag = function gtag() {
      // gtag.js reads the arguments object itself, so it must be pushed as-is.
      win.dataLayer.push(arguments);
    };
    win.gtag('js', new Date());
    win.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
    const script = doc.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    doc.head.appendChild(script);
  }
  win[disableFlag] = false;
}

/** Stops every hit (Google's own opt-out flag) while off the marketing pages. */
export function pauseAnalytics(win = window) {
  win[disableFlag] = true;
}

export function trackPageView(path, win = window) {
  if (!win.gtag || win[disableFlag]) return;
  win.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${win.location.origin}${path}`,
    page_title: win.document?.title,
  });
}
